import { useMemo } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { Calendar, Clock, ArrowLeft, ArrowUpRight, Tag } from "lucide-react";
import DOMPurify from "dompurify";
import Seo, { articleSchema } from "@/lib/seo";
import { Reveal } from "@/components/motion/Reveal";
import CTASection from "@/components/CTASection";
import { Skeleton, ErrorState } from "@/components/states";
import { useBlogPost, useBlogPosts } from "@/hooks/content";

const fmtDate = (d) => {
  if (!d) return null;
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

/**
 * Article bodies are HTML authored in the admin panel's rich-text editor.
 * Authors are authenticated admins, but the content is still sanitised before
 * it reaches dangerouslySetInnerHTML — defence in depth, and it also strips
 * anything malformed that a paste from Word might introduce.
 */
function sanitize(html) {
  return DOMPurify.sanitize(html || "", {
    ALLOWED_TAGS: [
      "p", "br", "strong", "b", "em", "i", "u", "s", "blockquote",
      "h2", "h3", "h4", "ul", "ol", "li", "a", "img", "figure",
      "figcaption", "code", "pre", "hr", "table", "thead", "tbody",
      "tr", "th", "td",
    ],
    ALLOWED_ATTR: ["href", "target", "rel", "src", "alt", "title", "loading"],
    // Block javascript:/data: URLs in links and images.
    ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|tel:|\/|#)/i,
    ADD_ATTR: ["target"],
  });
}

function ArticleSkeleton() {
  return (
    <div className="container-sky max-w-3xl py-20">
      <Skeleton className="h-4 w-24" />
      <Skeleton className="mt-8 h-10 w-full" />
      <Skeleton className="mt-3 h-10 w-3/4" />
      <Skeleton className="mt-8 aspect-[16/9] w-full rounded-[1.6rem]" />
      <div className="mt-10 space-y-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className={`h-4 ${i % 4 === 3 ? "w-2/3" : "w-full"}`} />
        ))}
      </div>
    </div>
  );
}

export default function BlogArticle() {
  const { slug } = useParams();
  const query = useBlogPost(slug);
  const allPosts = useBlogPosts();

  const post = query.data;
  const cleanContent = useMemo(() => sanitize(post?.content), [post?.content]);

  // Wait for the fetch to settle before deciding the article does not exist —
  // redirecting while still loading would bounce every direct link.
  if (query.isLoading) {
    return <ArticleSkeleton />;
  }

  if (query.isError) {
    return (
      <div className="container-sky max-w-3xl py-24">
        <ErrorState
          title="We couldn't load this article"
          description="Something went wrong on our side. Please try again in a moment."
          onRetry={query.refetch}
        />
        <div className="mt-8 text-center">
          <Link to="/blog" className="btn-outline-sky">
            <ArrowLeft className="h-4 w-4" /> Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const related = (allPosts.data || [])
    .filter((p) => p.slug !== slug)
    .slice(0, 3);

  const published = fmtDate(post.publishedAt);

  return (
    <>
      <Seo
        title={post.seo?.metaTitle || post.title}
        description={post.seo?.metaDescription || post.excerpt}
        path={`/blog/${post.slug}`}
        image={post.seo?.ogImage || post.featuredImage}
        type="article"
        schema={articleSchema({
          title: post.title,
          excerpt: post.excerpt,
          image: post.featuredImage,
          date: post.publishedAt,
          author: post.author,
        })}
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-navy-deep pt-[84px] lg:pt-[104px]">
        {post.featuredImage && (
          <img
            src={post.featuredImage}
            alt=""
            aria-hidden
            className="absolute inset-0 h-full w-full object-cover opacity-25"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-navy-deep via-navy-deep/85 to-navy/60" />
        <div className="container-sky relative py-16 lg:py-24">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-slate-300 transition-colors hover:text-white">
            <ArrowLeft className="h-4 w-4" /> Back to Blog
          </Link>
          {post.category && (
            <span className="mt-6 inline-block rounded-full bg-brand px-3.5 py-1.5 font-mono-tech text-[10px] uppercase tracking-wider text-white">
              {post.category}
            </span>
          )}
          <h1 className="mt-5 max-w-4xl font-heading text-3xl font-extrabold leading-[1.1] tracking-tight text-white sm:text-4xl lg:text-5xl">
            {post.title}
          </h1>
          <div className="mt-6 flex flex-wrap items-center gap-5 text-sm text-slate-300">
            {published && (
              <span className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-brand-light" /> {published}
              </span>
            )}
            {post.readingTime && (
              <span className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-brand-light" /> {post.readingTime}
              </span>
            )}
            {post.author && <span>By {post.author}</span>}
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="section-pad">
        <div className="container-sky max-w-3xl">
          {post.featuredImage && (
            <Reveal>
              <div className="overflow-hidden rounded-[1.6rem] shadow-xl">
                <img
                  src={post.featuredImage}
                  alt={post.title}
                  className="aspect-[16/9] w-full object-cover"
                />
              </div>
            </Reveal>
          )}

          {/*
            Typography for admin-authored HTML. Tailwind's typography plugin is
            not installed, so element styles are applied with arbitrary-variant
            selectors instead of adding a dependency for one page.
          */}
          <div
            className="mt-10 text-base leading-relaxed text-slate-700 sm:text-lg
              [&_p]:mb-6
              [&_h2]:mt-10 [&_h2]:mb-4 [&_h2]:font-heading [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-navy sm:[&_h2]:text-3xl
              [&_h3]:mt-8 [&_h3]:mb-3 [&_h3]:font-heading [&_h3]:text-xl [&_h3]:font-bold [&_h3]:text-navy
              [&_h4]:mt-6 [&_h4]:mb-2 [&_h4]:font-heading [&_h4]:text-lg [&_h4]:font-semibold [&_h4]:text-navy
              [&_ul]:mb-6 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:mb-6 [&_ol]:list-decimal [&_ol]:pl-6
              [&_li]:mb-2
              [&_a]:font-medium [&_a]:text-brand [&_a]:underline hover:[&_a]:text-navy
              [&_strong]:font-semibold [&_strong]:text-navy
              [&_blockquote]:my-6 [&_blockquote]:border-l-4 [&_blockquote]:border-brand [&_blockquote]:bg-surface [&_blockquote]:py-3 [&_blockquote]:pl-5 [&_blockquote]:pr-4 [&_blockquote]:italic
              [&_img]:my-6 [&_img]:rounded-xl
              [&_hr]:my-10 [&_hr]:border-slate-200
              [&_table]:my-6 [&_table]:w-full [&_table]:text-sm
              [&_th]:border [&_th]:border-slate-200 [&_th]:bg-surface [&_th]:p-2 [&_th]:text-left
              [&_td]:border [&_td]:border-slate-200 [&_td]:p-2"
            dangerouslySetInnerHTML={{ __html: cleanContent }}
          />

          {Array.isArray(post.tags) && post.tags.length > 0 && (
            <div className="mt-10 flex flex-wrap items-center gap-2.5 border-t border-slate-100 pt-8">
              <Tag className="h-4 w-4 text-brand" />
              {post.tags.map((t) => (
                <span key={t} className="rounded-full bg-surface px-3.5 py-1.5 text-xs font-medium text-navy">
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="pb-20">
          <div className="container-sky">
            <h2 className="font-heading text-2xl font-extrabold text-navy sm:text-3xl">Related Articles</h2>
            <div className="mt-8 grid gap-7 md:grid-cols-3">
              {related.map((p) => (
                <Link
                  key={p.id}
                  to={`/blog/${p.slug}`}
                  className="group overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-[0_4px_24px_rgba(3,56,106,0.05)] transition-all duration-500 hover:shadow-[0_18px_48px_rgba(10,126,201,0.12)]"
                >
                  {p.featuredImage && (
                    <div className="h-44 overflow-hidden">
                      <img
                        src={p.featuredImage}
                        alt={p.title}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    {p.category && (
                      <span className="font-mono-tech text-[10px] uppercase tracking-wider text-brand-ink">
                        {p.category}
                      </span>
                    )}
                    <h3 className="mt-2 font-heading text-base font-bold leading-snug text-navy group-hover:text-brand-ink">
                      {p.title}
                    </h3>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-ink">
                      Read More <ArrowUpRight className="h-4 w-4" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <CTASection />
    </>
  );
}
